import React, { useState, useRef, useEffect } from 'react';
import {
  Zap, BookOpen, Trophy, Flame, ChevronDown, Check, X,
  UserPlus, Mail, KeyRound, Loader2, ArrowLeft, Star,
  Target, Clock, Award, TrendingUp, Lock, Unlock, Clock3, Timer, Gauge,
  Eye, EyeOff, Sparkles, Rocket, Crown, Gem, Zap as ZapIcon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../services/supabaseClient';
import { subscribeToTier, isStripeConfigured, TierId } from '../services/stripe';
import { ACHIEVEMENTS, CATEGORY_NAMES, AchievementCategory, Achievement, LEVELS, calculateLevel } from '../data/achievements';
import { TIER_PRICING, TIER_BENEFITS, MembershipTier } from '../types/membership';
import { usePageMeta } from '../hooks/usePageMeta';

type Tier = 'free' | MembershipTier;

interface PricingTier {
  id: Tier;
  name: string;
  price: number;
  target: string;
  features: string[];
  accentColor: string;
  gradient: string;
}

// Derive pricing and features from canonical source
const pricingTiers: PricingTier[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    target: 'Everyone',
    features: TIER_BENEFITS.guest,
    accentColor: '#6b7280',
    gradient: 'from-slate-500 to-slate-600',
  },
  {
    id: 'starter',
    name: 'Starter',
    price: TIER_PRICING.starter,
    target: 'Casual',
    features: TIER_BENEFITS.starter,
    accentColor: '#22c55e',
    gradient: 'from-green-500 to-emerald-600',
  },
  {
    id: 'reader',
    name: 'Reader',
    price: TIER_PRICING.reader,
    target: 'Committed',
    features: TIER_BENEFITS.reader,
    accentColor: '#3b82f6',
    gradient: 'from-blue-500 to-violet-600',
  },
  {
    id: 'ultra',
    name: 'Ultra',
    price: TIER_PRICING.ultra,
    target: 'Power',
    features: TIER_BENEFITS.ultra,
    accentColor: '#9333ea',
    gradient: 'from-purple-500 to-pink-600',
  },
];

// Curated achievements for signup display (25 varied choices - 5 rows x 5 cols)
const signupAchievements = [
  // Row 1 - Speed
  { icon: '🐢', name: 'Crawler', desc: 'Complete your first session' },
  { icon: '🚶', name: 'Walking Pace', desc: 'Read at 50 WPM' },
  { icon: '🏃', name: 'Jogger', desc: 'Read at 100 WPM' },
  { icon: '💨', name: 'Sprinter', desc: 'Read at 250 WPM' },
  { icon: '⚡', name: 'Speed Demon', desc: 'Read at 500+ WPM' },
  // Row 2 - Volume
  { icon: '1️⃣', name: 'First Thousand', desc: 'Read 1,000 words' },
  { icon: '📚', name: 'Word Collector', desc: 'Read 10,000 words' },
  { icon: '📖', name: 'Avid Reader', desc: 'Complete 1 book' },
  { icon: '💯', name: 'Century Club', desc: 'Read 100 books' },
  { icon: '🌟', name: 'Million Word Club', desc: 'Read 1,000,000 words' },
  // Row 3 - Streaks
  { icon: '🔥', name: 'Getting Started', desc: 'Start a 3-day streak' },
  { icon: '📅', name: 'Week Warrior', desc: 'Maintain a 7-day streak' },
  { icon: '🏅', name: 'Three Week Champion', desc: 'Maintain a 21-day streak' },
  { icon: '🗓️', name: 'Monthly Master', desc: 'Maintain a 30-day streak' },
  { icon: '🏆', name: 'Year of Reading', desc: 'Maintain a 365-day streak' },
  // Row 4 - Challenges & Sessions
  { icon: '🎯', name: 'Challenger', desc: 'Complete 1 daily challenge' },
  { icon: '🏅', name: 'Challenge Champion', desc: 'Win 10 challenges' },
  { icon: '👣', name: 'First Steps', desc: 'Complete first reading session' },
  { icon: '📊', name: 'Regular Reader', desc: 'Read 50 sessions' },
  { icon: '⭐', name: 'Session Superstar', desc: 'Read 500 sessions' },
  // Row 5 - Variety, Time, Social, Milestone
  { icon: '🗺️', name: 'Genre Explorer', desc: 'Read 5 different genres' },
  { icon: '🌐', name: 'Bilingual Reader', desc: 'Read in 2 languages' },
  { icon: '⏱️', name: 'First Hour', desc: 'Read for 1 hour total' },
  { icon: '🕰️', name: 'Century of Hours', desc: 'Read for 100 hours' },
  { icon: '🏆', name: 'Record Breaker', desc: 'Beat your personal best' },
];

const achievements = [
  { icon: Zap, label: 'Speed Demon', desc: 'Read at 500+ WPM', color: 'text-yellow-500' },
  { icon: BookOpen, label: 'Bookworm', desc: 'Read 10 books', color: 'text-blue-500' },
  { icon: Flame, label: 'On Fire', desc: '30-day streak', color: 'text-orange-500' },
  { icon: Trophy, label: 'Champion', desc: 'Win a challenge', color: 'text-purple-500' },
  { icon: Target, label: 'Sharpshooter', desc: '100% accuracy', color: 'text-green-500' },
  { icon: Clock, label: 'Marathon', desc: 'Read for 2 hours', color: 'text-red-500' },
  { icon: Award, label: 'Collector', desc: 'Earn 10 badges', color: 'text-indigo-500' },
  { icon: TrendingUp, label: 'Rising Star', desc: 'Reach Level 5', color: 'text-cyan-500' },
];

// Feature matrix derived from TIER_FEATURES for comparison table
type FeatureMatrixRow = {
  feature: string;
  free: boolean | string;
  starter: boolean | string;
  reader: boolean | string;
  ultra: boolean | string;
};

const featureMatrix: FeatureMatrixRow[] = [
  { feature: 'Free Stories', free: '3 stories', starter: 'Unlimited', reader: 'Unlimited', ultra: 'Unlimited' },
  { feature: 'Public Library', free: false, starter: true, reader: true, ultra: true },
  { feature: 'Progress Tracking', free: 'Session', starter: 'Persistent', reader: 'Persistent', ultra: 'Persistent' },
  { feature: 'Speed Badges', free: true, starter: true, reader: true, ultra: true },
  { feature: 'XP & Level System', free: false, starter: false, reader: true, ultra: true },
  { feature: 'Daily Challenges', free: false, starter: false, reader: true, ultra: true },
  { feature: 'Streaks', free: false, starter: false, reader: true, ultra: true },
  { feature: '.txt Upload', free: false, starter: true, reader: true, ultra: true },
  { feature: '.epub Upload', free: false, starter: false, reader: true, ultra: true },
  { feature: 'Custom Themes', free: false, starter: false, reader: true, ultra: true },
  { feature: 'Custom Fonts', free: false, starter: false, reader: true, ultra: true },
  { feature: 'Analytics', free: false, starter: false, reader: true, ultra: true },
  { feature: 'Cloud Sync', free: false, starter: false, reader: false, ultra: true },
  { feature: 'History Export', free: false, starter: false, reader: false, ultra: true },
  { feature: 'Streak Freezes', free: false, starter: false, reader: false, ultra: true },
];

const SignupPage: React.FC = () => {
  usePageMeta({
    title: 'Speed Reading App Signup & Pricing | Flash-Read',
    description: 'Sign up for Flash-Read speed reading app. Choose from Free, Starter, Reader, or Ultra plans. Unlock 500+ stories, achievements, streaks, and progress tracking.',
  });

  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState<Tier | null>(null);
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<'google' | null>(null);
  const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const authFormRef = useRef<HTMLDivElement>(null);

  // Password strength calculation
  const getPasswordStrength = (pwd: string): { level: number; label: string; color: string } => {
    if (!pwd) return { level: 0, label: '', color: '' };
    let strength = 0;
    if (pwd.length >= 6) strength++;
    if (pwd.length >= 8) strength++;
    if (/[A-Z]/.test(pwd)) strength++;
    if (/[0-9]/.test(pwd)) strength++;
    if (/[^A-Za-z0-9]/.test(pwd)) strength++;
    
    if (strength <= 1) return { level: 1, label: 'Weak', color: 'bg-red-500' };
    if (strength <= 2) return { level: 2, label: 'Fair', color: 'bg-orange-500' };
    if (strength <= 3) return { level: 3, label: 'Good', color: 'bg-yellow-500' };
    if (strength <= 4) return { level: 4, label: 'Strong', color: 'bg-green-500' };
    return { level: 5, label: 'Very Strong', color: 'bg-green-600' };
  };

  const passwordStrength = getPasswordStrength(password);

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError(null);
    }
  };

  const handleSelectTier = (tier: Tier) => {
    setSelectedTier(tier);
    setShowAuthForm(true);
    setTimeout(() => {
      authFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleCardClick = (tier: Tier) => {
    setSelectedTier(tier);
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    setEmailError(null);
    setPasswordError(null);

    // Validate email
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Validate password match
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (!isSupabaseConfigured) {
      setMessage({ type: 'error', text: 'Supabase credentials missing. Check .env file.' });
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            selected_tier: selectedTier || 'starter',
          }
        }
      });
      if (error) throw error;
      
      // Update user profile with GUEST (free) tier initially
      // IMPORTANT: User is set to 'guest' BEFORE payment completes
      // The Stripe webhook will upgrade the tier AFTER successful payment
      if (data.user) {
        console.log('User created, data.user.id:', data.user.id);
        
        // Always start with 'guest' (free) tier - payment not yet complete
        // Webhook will upgrade to paid tier after checkout.session.completed
        const initialTier: 'guest' = 'guest';
        
        await supabase
          .from('user_profiles')
          .upsert({
            id: data.user.id,
            email: email,
            membership_tier: initialTier,
            created_at: new Date().toISOString(),
          });
        console.log('User profile upserted with GUEST (free) tier - payment pending');
        
        // If Stripe is configured and user selected a PAID tier, initiate checkout
        // Free tier users skip Stripe entirely
        if (isStripeConfigured() && selectedTier && selectedTier !== 'free') {
          console.log('Stripe configured, selectedTier:', selectedTier);
          try {
            await subscribeToTier(selectedTier as TierId, data.user.id, email);
            console.log('Stripe subscribeToTier completed');
          } catch (stripeError) {
            console.error('Stripe checkout error:', stripeError);
            // Continue even if Stripe fails - user can upgrade later
          }
        } else {
          console.log('Free tier selected or Stripe not configured. selectedTier:', selectedTier);
        }
      } else {
        console.log('No user data returned from signUp');
      }
      
      setMessage({ type: 'success', text: 'Account created! Please check your email to confirm.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'An error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google') => {
    if (!isSupabaseConfigured) {
      setMessage({ type: 'error', text: 'Supabase credentials missing. Check .env file.' });
      return;
    }

    setSocialLoading(provider);
    setMessage(null);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: window.location.origin,
          queryParams: {
            selected_tier: selectedTier || 'starter',
          }
        }
      });
      if (error) throw error;
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || `Failed to sign in with ${provider}` });
    } finally {
      setSocialLoading(null);
    }
  };

  const handleFreeStart = () => {
    setSelectedTier('starter');
    setShowAuthForm(true);
    setTimeout(() => {
      authFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  return (
    <>
      <style>{`
        @keyframes fade-in {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
        `}</style>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300">

      {/* Hero Text */}
      <section className="pt-24 md:pt-32 pb-8 md:pb-12 px-4 bg-slate-50 dark:bg-slate-950">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white mb-6 leading-tight">
            Unlock{' '}
            <span className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
              Speed Reading
            </span>
            : Choose Your RSVP Reading Plan
          </h1>
          <div className="stories-intro p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-100 dark:border-blue-800 max-w-3xl mx-auto mb-4">
            <p className="text-base md:text-lg text-slate-700 dark:text-slate-300 leading-relaxed text-center">
              Your destination for <span className="keyword-highlight">speed reading signup</span> and premium RSVP training.<br />
              Choose your <span className="keyword-highlight">RSVP reading plan</span> — from free to ultra.<br />
              Compare <span className="keyword-highlight">speed reading pricing</span> and <span className="keyword-highlight">unlock achievements</span>.<br />
              Experience the ultimate <span className="keyword-highlight">reading gamification</span> app — <span className="keyword-highlight">free reading app</span> to start.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-8 py-4 bg-slate-900 text-white font-bold rounded-full hover:bg-slate-700 transition-all transform hover:scale-105 hover:shadow-xl hover:shadow-slate-500/25 shadow-xl"
            >
              View Plans
              <ChevronDown className="w-5 h-5" />
            </button>
            <button
              onClick={handleFreeStart}
              className="inline-flex items-center gap-2 px-8 py-4 bg-transparent border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-bold rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-slate-400 dark:hover:border-slate-500 transition-all"
            >
              <Rocket className="w-5 h-5" />
              Start Now
            </button>
          </div>
        </div>
      </section>

      {/* Gamification Preview - Level Up Your Reading */}
      <section className="pt-10 pb-10 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 border border-blue-200 dark:border-blue-700 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-yellow-500" />
              <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">Unlock 100+ Achievements</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Level Up Your Reading
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              Earn XP, unlock achievements, and build streaks as you read.
            </p>
            <p className="text-slate-600 dark:text-slate-400">
              Master 10 levels with {ACHIEVEMENTS.length}+ achievements to level up.
            </p>
          </div>

          {/* Level Preview with Achievements */}
          <div className="mb-16">

            {/* Current Level Info - Enhanced */}
            <div className="max-w-lg mx-auto bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-xl shadow-slate-500/10 mb-8">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-2 rounded-lg">
                    <Crown className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className="text-lg font-bold text-slate-900 dark:text-white">
                      Level {LEVELS[2].level}: {LEVELS[2].title}
                    </span>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Current Progress</p>
                  </div>
                </div>
                <span className="text-lg font-bold text-blue-500">1,100 XP</span>
              </div>
              <div className="w-full h-4 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full transition-all duration-1000"
                  style={{ width: '45%' }}
                />
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-3 text-center flex items-center justify-center gap-2">
                <ZapIcon className="w-4 h-4 text-yellow-500" />
                450 XP to next level • Unlock achievements to earn XP
              </p>
            </div>

            {/* Level Display - Enhanced with glow effects */}
            <div className="flex items-center justify-center gap-1 md:gap-2 mb-8">
              {LEVELS.map((level, idx) => (
                <div
                  key={level.level}
                  className={`relative group w-10 md:w-14 h-14 md:h-16 rounded-xl flex flex-col items-center justify-center font-bold text-xs md:text-sm transition-all duration-300 cursor-pointer hover:scale-110 hover:-translate-y-2 ${
                    level.level <= 3
                      ? 'bg-gradient-to-b from-yellow-400 to-yellow-600 text-yellow-900 shadow-lg shadow-yellow-500/30 hover:shadow-yellow-500/60'
                      : level.level <= 6
                      ? 'bg-gradient-to-b from-blue-400 to-blue-600 text-blue-900 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/60'
                      : level.level <= 9
                      ? 'bg-gradient-to-b from-purple-400 to-purple-600 text-purple-900 shadow-lg shadow-purple-500/30 hover:shadow-purple-500/60'
                      : 'bg-gradient-to-b from-pink-500 to-red-600 text-white shadow-lg shadow-pink-500/30 hover:shadow-pink-500/60'
                  }`}
                  title={`Level ${level.level}: ${level.title}`}
                >
                  {/* Glow effect */}
                  <div className={`absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                    level.level <= 3 ? 'bg-yellow-400' :
                    level.level <= 6 ? 'bg-blue-400' :
                    level.level <= 9 ? 'bg-purple-400' : 'bg-pink-400'
                  } blur-xl`} />
                  <span className="relative z-10">{level.level}</span>
                  <span className="absolute -bottom-6 text-[10px] text-slate-500 dark:text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    {level.title}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Achievement Grid - 4x4 Layout */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                100 Achievements to Unlock
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                {ACHIEVEMENTS.length} achievements across {Object.keys(CATEGORY_NAMES).length} categories
              </p>
            </div>
            <div className="grid grid-cols-4 gap-4 max-w-4xl mx-auto">
              {signupAchievements.slice(0, 16).map((achievement, idx) => (
                <div
                  key={idx}
                  className="group relative bg-white dark:bg-slate-800 rounded-xl p-4 text-center border border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl flex flex-col h-28"
                >
                  {/* Glow on hover */}
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative z-10 flex flex-col h-full">
                    <span className="text-3xl block mb-1 transform group-hover:scale-110 transition-transform">{achievement.icon}</span>
                    <p className="font-bold text-slate-900 dark:text-white text-sm">{achievement.name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-auto">{achievement.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gamification Features - Enhanced cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="group relative bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-2xl p-6 border border-orange-200 dark:border-orange-800 hover:border-orange-400 dark:hover:border-orange-600 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:shadow-orange-500/20">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-orange-500/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-br from-orange-400 to-red-500 p-3 rounded-xl shadow-lg shadow-orange-500/30">
                    <Flame className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">Daily Challenges</h3>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  Complete daily reading challenges to earn bonus XP and keep your streak alive.
                </p>
              </div>
            </div>

            <div className="group relative bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-6 border border-purple-200 dark:border-purple-800 hover:border-purple-400 dark:hover:border-purple-600 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:shadow-purple-500/20">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-br from-purple-400 to-pink-500 p-3 rounded-xl shadow-lg shadow-purple-500/30">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">Streak Multipliers</h3>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  Build reading streaks for up to 2x XP multiplier. Don't break the chain!
                </p>
              </div>
            </div>

            <div className="group relative bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-2xl p-6 border border-yellow-200 dark:border-yellow-800 hover:border-yellow-400 dark:hover:border-yellow-600 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:shadow-yellow-500/20">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-yellow-500/5 to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-br from-yellow-400 to-amber-500 p-3 rounded-xl shadow-lg shadow-yellow-500/30">
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">Achievements</h3>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  Unlock 100+ achievements across 10 categories. Earn XP and level up!
                </p>
              </div>
            </div>

            <div className="group relative bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-2xl p-6 border border-emerald-200 dark:border-emerald-800 hover:border-emerald-400 dark:hover:border-emerald-600 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:shadow-emerald-500/20">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500/5 to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-br from-emerald-400 to-teal-500 p-3 rounded-xl shadow-lg shadow-emerald-500/30">
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">Personal Bests</h3>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  Track your reading progress and beat your own records. Can you improve your speed or read more words?
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 bg-slate-50 dark:bg-slate-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Select Your Tier
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              Start now, upgrade anytime. Cancel anytime.
            </p>
          </div>


          {/* Free Tier Banner - Horizontal above paid tiers */}
          <div
            onClick={() => handleCardClick('free')}
            className={`mb-8 cursor-pointer rounded-2xl border-2 transition-all duration-300 hover:shadow-lg ${
              selectedTier === 'free'
                ? 'border-slate-500 shadow-md bg-slate-50 dark:bg-slate-800/50'
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-600'
            }`}
          >
            <div className="flex flex-col sm:flex-row items-center justify-between p-6 gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-br from-slate-500 to-slate-600 p-3 rounded-xl shadow-lg">
                  <ZapIcon className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">Free</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">For {pricingTiers[0].target} Readers</p>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <ul className="flex flex-wrap gap-3 text-sm text-slate-600 dark:text-slate-300">
                  {pricingTiers[0].features.slice(0, 3).map((feature) => (
                    <li key={feature} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-slate-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectTier('free');
                  }}
                  className={`py-3 px-6 rounded-xl font-bold transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] whitespace-nowrap ${
                    selectedTier === 'free'
                      ? 'bg-gradient-to-r from-slate-500 to-slate-600 text-white'
                      : 'bg-slate-900 text-white hover:bg-slate-700'
                  }`}
                 >
                   Get Started
                 </button>
              </div>
            </div>
          </div>

          {/* Paid Pricing Cards - Horizontal scroll on mobile */}
          <div className="relative">
            {/* Scroll indicator for mobile */}
            <div className="md:hidden absolute right-4 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
              <div className="bg-slate-900/50 dark:bg-white/50 backdrop-blur-sm p-2 rounded-full">
                <ChevronDown className="w-4 h-4 text-white dark:text-slate-900 rotate-[-90deg]" />
              </div>
            </div>
            <div className="flex overflow-x-auto pb-8 gap-6 md:grid md:grid-cols-3 md:gap-8 snap-x snap-mandatory md:pb-0 -mx-4 px-4 md:mx-0 md:px-0">
            {pricingTiers.filter((tier) => tier.id !== 'free').map((tier) => (
              <div key={tier.id} className="relative">
                {tier.id === 'reader' && (
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-20">
                    <span className="relative inline-flex items-center gap-1 bg-gradient-to-r from-blue-500 via-violet-500 to-purple-600 text-white px-5 py-2 rounded-full text-sm font-bold shadow-lg shadow-blue-500/40 animate-pulse">
                      <Star className="w-4 h-4 fill-current" />
                      Best Value
                      <Star className="w-4 h-4 fill-current" />
                    </span>
                  </div>
                )}
                <div
                  onClick={() => handleCardClick(tier.id)}
                  className={`relative flex-shrink-0 w-[85%] md:w-auto snap-center bg-white dark:bg-slate-900 rounded-2xl transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl cursor-pointer ${
                    selectedTier === tier.id
                      ? (tier.id === 'starter' ? 'border-4 border-green-500 shadow-[0_0_40px_rgba(0,0,0,0.5)] ring-8 ring-green-500/50' : tier.id === 'reader' ? 'border-4 border-blue-500 shadow-[0_0_40px_rgba(0,0,0,0.5)] ring-8 ring-blue-500/50' : 'border-4 border-purple-500 shadow-[0_0_40px_rgba(0,0,0,0.5)] ring-8 ring-purple-500/50')
                      : 'border-2 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  <div className={`h-2 rounded-t-2xl bg-gradient-to-r ${tier.gradient}`} />

                  <div className={`p-8 ${tier.id === 'reader' ? 'pt-8' : ''}`}>
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 mb-4">
                      {tier.id === 'starter' && <BookOpen className={`w-8 h-8 ${tier.accentColor}`} />}
                      {tier.id === 'reader' && <Crown className={`w-8 h-8 ${tier.accentColor}`} />}
                      {tier.id === 'ultra' && <Gem className={`w-8 h-8 ${tier.accentColor}`} />}
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                      {tier.name}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      For {tier.target} Readers
                    </p>
                  </div>

                  <div className="text-center mb-6">
                    <span className="text-5xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-300 bg-clip-text text-transparent">
                      ${tier.price}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400">/month</span>
                  </div>


                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-300">
                        <div className={`mt-0.5 p-0.5 rounded-full bg-gradient-to-r ${tier.gradient} bg-opacity-20`}>
                          <Check className={`w-4 h-4 flex-shrink-0 ${tier.id === 'starter' ? 'text-green-500' : tier.id === 'reader' ? 'text-blue-500' : 'text-purple-500'}`} />
                        </div>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleSelectTier(tier.id)}
                    className={`w-full py-4 px-6 rounded-xl font-bold transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] ${
                      selectedTier === tier.id
                        ? `bg-gradient-to-r ${tier.gradient} text-white hover:opacity-90`
                        : 'bg-slate-900 text-white hover:bg-slate-700'
                    }`}
                  >
                    Get Started
                  </button>
                </div>
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="py-20 px-4 bg-slate-100 dark:bg-slate-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 dark:text-white mb-4">
            Feature Comparison
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-center mb-12">
            Choose the plan that fits your reading goals
          </p>

          <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-700 shadow-xl">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800">
                  <th className="text-left py-5 px-6 font-bold text-slate-900 dark:text-white">Feature</th>
                  <th className="text-center py-5 px-6 font-bold text-slate-600">Free</th>
                  <th className="text-center py-5 px-6 font-bold text-green-600">Starter</th>
                  <th className="text-center py-5 px-6 font-bold text-blue-600">Reader</th>
                  <th className="text-center py-5 px-6 font-bold text-purple-600">Ultra</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {featureMatrix.map((row, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    <td className="py-4 px-6 text-slate-700 dark:text-slate-300 font-medium">{row.feature}</td>
                    <td className="text-center py-4 px-6">
                      {typeof row.free === 'boolean' ? (
                        row.free ? (
                          <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700">
                            <Check className="w-5 h-5 text-slate-500" />
                          </div>
                        ) : (
                          <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700">
                            <X className="w-4 h-4 text-slate-400" />
                          </div>
                        )
                      ) : (
                        <span className="text-slate-600 dark:text-slate-400 font-medium">{row.free}</span>
                      )}
                    </td>
                    <td className="text-center py-4 px-6">
                      {typeof row.starter === 'boolean' ? (
                        row.starter ? (
                          <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30">
                            <Check className="w-5 h-5 text-green-500" />
                          </div>
                        ) : (
                          <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700">
                            <X className="w-4 h-4 text-slate-400" />
                          </div>
                        )
                      ) : (
                        <span className="text-slate-600 dark:text-slate-400 font-medium">{row.starter}</span>
                      )}
                    </td>
                    <td className="text-center py-4 px-6">
                      {typeof row.reader === 'boolean' ? (
                        row.reader ? (
                          <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30">
                            <Check className="w-5 h-5 text-blue-500" />
                          </div>
                        ) : (
                          <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700">
                            <X className="w-4 h-4 text-slate-400" />
                          </div>
                        )
                      ) : (
                        <span className="text-slate-600 dark:text-slate-400 font-medium">{row.reader}</span>
                      )}
                    </td>
                    <td className="text-center py-4 px-6">
                      {typeof row.ultra === 'boolean' ? (
                        row.ultra ? (
                          <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30">
                            <Check className="w-5 h-5 text-purple-500" />
                          </div>
                        ) : (
                          <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700">
                            <X className="w-4 h-4 text-slate-400" />
                          </div>
                        )
                      ) : (
                        <span className="text-slate-600 dark:text-slate-400 font-medium">{row.ultra}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Signup Form */}
      {showAuthForm && (
        <section ref={authFormRef} className="py-20 px-4 bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-900 dark:to-slate-950">
          <div className="max-w-md mx-auto">
            <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 p-8">
              <button
                onClick={() => setShowAuthForm(false)}
                className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 mb-6 transition-colors group"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                Back to plans
              </button>

              <div className="text-center mb-8">
                <div className="bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 p-5 rounded-2xl mb-4 inline-flex">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 rounded-xl">
                    <UserPlus className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                  Create Your Account
                </h2>
                <p className="text-slate-600 dark:text-slate-400 mt-2 text-sm">
                  Selected: <span className="font-bold text-blue-500">{selectedTier?.toUpperCase()}</span> plan
                </p>
              </div>

              {/* Social Login - More Prominent */}
              <div className="mb-6">
                <button
                  type="button"
                  onClick={() => handleSocialLogin('google')}
                  disabled={socialLoading !== null}
                  className="flex items-center justify-center gap-2 py-3 px-4 w-full bg-white dark:bg-slate-700 border-2 border-slate-200 dark:border-slate-600 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-600 hover:border-slate-300 dark:hover:border-slate-500 transition-all disabled:opacity-50 group"
                >
                  {socialLoading === 'google' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <svg className="w-5 h-5 group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Google</span>
                </button>
              </div>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200 dark:border-slate-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white dark:bg-slate-800 text-slate-500">
                    Or sign up with email
                  </span>
                </div>
              </div>

              <form onSubmit={handleAuth} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      className={`w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-slate-900 border-2 rounded-xl focus:ring-0 outline-none transition-all text-slate-900 dark:text-white placeholder-slate-400 ${
                        emailError 
                          ? 'border-red-400 focus:border-red-500' 
                          : 'border-slate-200 dark:border-slate-700 focus:border-blue-500'
                      }`}
                      placeholder="you@example.com"
                    />
                  </div>
                  {emailError && (
                    <div className="flex items-center gap-1 mt-2 text-red-500">
                      <X className="w-4 h-4" />
                      <p className="text-sm">{emailError}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      minLength={6}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-12 pr-14 py-3 bg-slate-50 dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700 rounded-xl focus:ring-0 focus:border-blue-500 outline-none transition-all text-slate-900 dark:text-white placeholder-slate-400"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {/* Password Strength Indicator */}
                  {password && (
                    <div className="mt-3">
                      <div className="flex gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <div
                            key={level}
                            className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                              level <= passwordStrength.level 
                                ? passwordStrength.color 
                                : 'bg-slate-200 dark:bg-slate-700'
                            }`}
                          />
                        ))}
                      </div>
                      <p className={`text-xs font-medium ${
                        passwordStrength.level <= 1 ? 'text-red-500' :
                        passwordStrength.level <= 2 ? 'text-orange-500' :
                        passwordStrength.level <= 3 ? 'text-yellow-500' :
                        'text-green-500'
                      }`}>
                        Password strength: {passwordStrength.label}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      minLength={6}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`w-full pl-12 pr-14 py-3 bg-slate-50 dark:bg-slate-900 border-2 rounded-xl focus:ring-0 outline-none transition-all text-slate-900 dark:text-white placeholder-slate-400 ${
                        passwordError && confirmPassword
                          ? 'border-red-400 focus:border-red-500' 
                          : 'border-slate-200 dark:border-slate-700 focus:border-blue-500'
                      }`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {passwordError && confirmPassword && (
                    <div className="flex items-center gap-1 mt-2 text-red-500">
                      <X className="w-4 h-4" />
                      <p className="text-sm">{passwordError}</p>
                    </div>
                  )}
                </div>

                {message && (
                  <div
                    className={`text-sm p-4 rounded-xl flex items-center gap-2 ${
                      message.type === 'error'
                        ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
                        : 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400'
                    }`}
                  >
                    {message.type === 'error' ? <X className="w-5 h-5" /> : <Check className="w-5 h-5" />}
                    {message.text}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-4 px-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Creating Account...</span>
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-5 h-5" />
                      <span>Create Account</span>
                    </>
                  )}
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-slate-100 dark:border-slate-700">
                <div className="text-center">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Already have an account?{' '}
                    <button 
                      onClick={() => navigate('/login')} 
                      className="font-semibold text-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      Sign In
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

    </div>
    </>
  );
};

export default SignupPage;
